// components/ui/Toast.tsx
/**
 * @description Toast notification component with context provider and action button support
 */
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

type ToastType = 'success' | 'error' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  action?: ToastAction;
  onClose: () => void;
}

export function Toast({ message, type = 'success', duration = 3000, action, onClose }: ToastProps) {
  useEffect(() => {
    // Extend duration if there's an action button
    const actualDuration = action ? duration + 2000 : duration;
    const timer = setTimeout(onClose, actualDuration);
    return () => clearTimeout(timer);
  }, [duration, action, onClose]);

  const icons = {
    success: <CheckCircle className="text-green-500 flex-shrink-0" size={20} />,
    error: <AlertCircle className="text-red-500 flex-shrink-0" size={20} />,
    info: <Info className="text-blue-500 flex-shrink-0" size={20} />,
  };

  const backgrounds = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    info: 'bg-blue-50 border-blue-200',
  };

  const actionButtonStyles = {
    success: 'bg-green-600 hover:bg-green-700 text-white',
    error: 'bg-red-600 hover:bg-red-700 text-white',
    info: 'bg-blue-600 hover:bg-blue-700 text-white',
  };

  return (
    <div
      className={cn(
        'fixed bottom-20 left-1/2 -translate-x-1/2 z-[200]',
        'flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg',
        'max-w-[90vw] animate-slide-up',
        backgrounds[type]
      )}
    >
      {icons[type]}
      <span className="text-sm font-medium text-gray-800 flex-1">{message}</span>

      {action && (
        <button
          onClick={() => {
            action.onClick();
            onClose();
          }}
          className={cn(
            'px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap',
            actionButtonStyles[type]
          )}
        >
          {action.label}
        </button>
      )}

      <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
        <X size={16} />
      </button>
    </div>
  );
}

// Toast Context
interface ToastContextType {
  showToast: (message: string, type?: ToastType, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

interface ToastState {
  message: string;
  type: ToastType;
  action?: ToastAction;
  id: number;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback((message: string, type: ToastType = 'success', action?: ToastAction) => {
    setToast({ message, type, action, id: Date.now() });
  }, []);

  const handleClose = useCallback(() => {
    setToast(null);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          action={toast.action}
          onClose={handleClose}
        />
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
